import { apiUrl, currentUserId, currentUserQuery } from "./auth";
import type { CrmFiche } from "./types";

export type CrmMate = { id: number; name: string; society?: string };

let mates: CrmMate[] = [];
let book: number[] = [];
let writer: { id: number; name: string } = { id: 0, name: "" };

export async function ensureWriter(): Promise<{ id: number; name: string }> {
  if (writer.id > 0 && writer.name) return writer;
  const localId = Number(currentUserId()) || 0;
  const localName = mates.find((mate) => mate.id === localId)?.name || "";
  if (localId > 0 && localName) {
    writer = { id: localId, name: localName };
    return writer;
  }
  try {
    const res = await fetch(apiUrl("ping.php") + currentUserQuery(), {
      credentials: "include",
      cache: "no-store",
      headers: authHeaders(),
    });
    const data = await read(res);
    const id = Number(data.userId) || localId;
    const name = String(data.username || localName || "").trim();
    if (id > 0 && name) writer = { id, name };
    else if (id > 0) writer = { id, name: writer.name };
  } catch {
    /* le nom local reste utilisé */
  }
  return writer.id > 0 ? writer : { id: localId, name: localName };
}

export function knownWriterId(): number {
  return writer.id || Number(currentUserId()) || 0;
}

export function crmMates(): CrmMate[] {
  return mates;
}

export function crmBook(): number[] {
  return book;
}

function rememberMates(raw: unknown) {
  if (!Array.isArray(raw)) return;
  mates = raw.flatMap((row) => {
    const id = Number((row as { id?: number }).id) || 0;
    const name = String((row as { name?: string }).name || "").trim();
    const society = String((row as { society?: string }).society || "").trim();
    return id > 0 && name !== "" ? [{ id, name, ...(society ? { society } : {}) }] : [];
  });
}

function rememberBook(raw: unknown) {
  book = Array.isArray(raw) ? raw.map((id) => Number(id) || 0).filter((id) => id > 0) : [];
}

function asFiche(row: CrmFiche): CrmFiche {
  const tags = Array.isArray(row.tags) ? row.tags.map((tag) => String(tag).trim()).filter(Boolean) : [];
  return {
    ...row,
    kind: row.kind === "particulier" ? "particulier" : "societe",
    scope: row.scope === "interne" ? "interne" : "client",
    planning_team: Array.isArray(row.planning_team) ? row.planning_team.map((id) => Number(id) || 0).filter((id) => id > 0) : [],
    siren: String(row.siren || ""),
    siret: String(row.siret || ""),
    origin: String(row.origin || ""),
    tags,
    sites: Array.isArray(row.sites) ? row.sites.map((site) => ({
      label: String(site?.label || ""),
      address: String(site?.address || ""),
      postal_code: String(site?.postal_code || ""),
      city: String(site?.city || ""),
    })) : [],
    fields: Array.isArray(row.fields) ? row.fields.map((field) => ({
      label: String(field?.label || ""),
      value: String(field?.value || ""),
    })).filter((field) => field.label !== "") : [],
    consent: row.consent === true || row.consent === 1 || row.consent === "1",
    legal_basis: String(row.legal_basis || ""),
    consent_on: String(row.consent_on || "").slice(0, 10),
    owner_user_id: Number(row.owner_user_id) || 0,
    owner_name: String(row.owner_name || ""),
    created_by: Number(row.created_by) || 0,
    author_name: String(row.author_name || ""),
    shared_with: Array.isArray(row.shared_with) ? row.shared_with.map((id) => Number(id) || 0).filter((id) => id > 0) : [],
    desk_private: row.desk_private === true || row.desk_private === 1 || row.desk_private === "1",
    mine: row.mine === true || row.mine === 1 || row.mine === "1",
    via: row.via === "fiche" || row.via === "bureau" ? row.via : "mine",
    circle_name: String(row.circle_name || "").trim(),
  };
}

async function read(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function authHeaders(extra?: Record<string, string>): HeadersInit {
  const id = currentUserId();
  return { ...(id ? { "X-User-Id": id } : {}), ...extra };
}

export async function listArchived(): Promise<CrmFiche[]> {
  const res = await fetch(apiUrl("archive.php") + currentUserQuery(), {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Archives indisponibles"));
  }
  return sortFiches(((data.fiches as CrmFiche[]) || []).map(asFiche), await ficheOrder(true));
}

export async function archiveFiche(id: number): Promise<void> {
  await postArchive(id, "archive");
}

export async function deleteFiche(id: number): Promise<void> {
  const res = await fetch(apiUrl("fiche.php") + currentUserQuery(), {
    method: "DELETE",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ id }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Suppression impossible"));
  }
}

export async function restoreFiche(id: number): Promise<void> {
  await postArchive(id, "restore");
}

async function postArchive(id: number, action: "archive" | "restore"): Promise<void> {
  const res = await fetch(apiUrl("archive.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ id, action }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Archivage impossible"));
  }
}

export async function saveFicheOrder(ids: number[]): Promise<void> {
  const res = await fetch(apiUrl("order.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ ids }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Ordre non enregistré"));
  }
}

export async function listFiches(): Promise<CrmFiche[]> {
  const res = await fetch(apiUrl("fiche.php") + currentUserQuery(), {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Fiches indisponibles"));
  }
  rememberMates(data.team);
  rememberBook(data.book);
  return sortFiches(((data.fiches as CrmFiche[]) || []).map(asFiche), await ficheOrder(false));
}

export async function shareFiche(id: number, sharedWith: number[], deskPrivate: boolean): Promise<{ fiche: CrmFiche; coffre: string }> {
  const res = await fetch(apiUrl("fiche.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ action: "share", id, shared_with: sharedWith, desk_private: deskPrivate }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false || !data.fiche) {
    throw new Error(String(data.message || "Partage impossible"));
  }
  return { fiche: asFiche(data.fiche as CrmFiche), coffre: typeof data.coffre === "string" ? data.coffre : "" };
}

export async function saveBook(sharedWith: number[]): Promise<string> {
  const res = await fetch(apiUrl("fiche.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ action: "book", shared_with: sharedWith }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Partage du bureau impossible"));
  }
  rememberBook(data.book);
  return typeof data.coffre === "string" ? data.coffre : "";
}

async function ficheOrder(archived: boolean): Promise<number[]> {
  const query = currentUserQuery();
  const archivedQuery = archived ? (query ? `${query}&archived=1` : "?archived=1") : query;
  const res = await fetch(apiUrl("order.php") + archivedQuery, {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) return [];
  return Array.isArray(data.ids) ? data.ids.map((id) => Number(id)) : [];
}

function sortFiches(rows: CrmFiche[], ids: number[]): CrmFiche[] {
  if (ids.length === 0) return rows;
  const rank = new Map(ids.map((id, index) => [id, index]));
  return [...rows].sort((a, b) => (rank.get(a.id) ?? ids.length) - (rank.get(b.id) ?? ids.length) || a.name.localeCompare(b.name, "fr"));
}

export async function openFiche(coffrePath: string, ownerId = 0, name = "", scope: "client" | "interne" = "client"): Promise<CrmFiche> {
  const res = await fetch(apiUrl("fiche.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ coffre_path: coffrePath, coffre_owner_id: ownerId, ...(name.trim() ? { name: name.trim() } : {}), ...(scope === "interne" ? { scope: "interne" } : {}) }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false || !data.fiche) {
    throw new Error(String(data.message || "Dossier non retenu"));
  }
  return asFiche(data.fiche as CrmFiche);
}

export async function saveFiche(fiche: CrmFiche): Promise<CrmFiche> {
  const res = await fetch(apiUrl("fiche.php") + currentUserQuery(), {
    method: "PATCH",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(fiche),
  });
  const data = await read(res);
  return asFiche(data.fiche as CrmFiche);
}

export async function mergeFiche(keep: number, drop: number): Promise<CrmFiche> {
  const res = await fetch(apiUrl("fiche.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ action: "merge", keep, drop }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false || !data.fiche) throw new Error(String(data.message || "Fusion impossible"));
  return asFiche(data.fiche as CrmFiche);
}

export async function noteVisit(ficheId: number): Promise<void> {
  await fetch(apiUrl("suivi.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ fiche: ficheId, action: "visit" }),
  });
}

export async function exportFichePack(ficheId: number): Promise<Record<string, unknown>> {
  const res = await fetch(`${apiUrl("privacy.php")}${currentUserQuery()}${currentUserQuery() ? "&" : "?"}fiche=${ficheId}`, {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false || !data.export) throw new Error(String(data.message || "Export impossible"));
  return data.export as Record<string, unknown>;
}

export async function forgetFiche(ficheId: number): Promise<void> {
  const res = await fetch(apiUrl("privacy.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ id: ficheId }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) throw new Error(String(data.message || "Oubli impossible"));
}

export type FicheContact = { name: string; email: string; phone: string };

export async function listFicheContacts(ficheId: number): Promise<FicheContact[]> {
  const query = currentUserQuery();
  const res = await fetch(`${apiUrl("contact.php")}${query}${query ? "&" : "?"}fiche=${ficheId}`, {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) return [];
  const rows = Array.isArray(data.contacts) ? data.contacts : [];
  return rows.map((row) => {
    const item = row as { name?: string; email?: string; phone?: string };
    return { name: String(item.name || ""), email: String(item.email || ""), phone: String(item.phone || "") };
  });
}

export type SireneMatch = {
  name: string;
  siret: string;
  siren: string;
  address: string;
  postal_code: string;
  city: string;
  status: "active" | "ceased" | "closed";
};

export async function lookupSirene(query: string): Promise<SireneMatch[]> {
  const res = await fetch(`${apiUrl("sirene.php")}${currentUserQuery()}${currentUserQuery() ? "&" : "?"}q=${encodeURIComponent(query)}`, {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Recherche SIRET impossible"));
  }
  const rows = Array.isArray(data.matches) ? data.matches : [];
  return rows.flatMap((row) => {
    const item = row as Partial<SireneMatch>;
    const siret = String(item.siret || "").replace(/\D/g, "");
    const siren = String(item.siren || "").replace(/\D/g, "");
    if (siret.length !== 14 || siren.length !== 9) return [];
    const status = item.status === "ceased" || item.status === "closed" ? item.status : "active";
    return [{
      name: String(item.name || ""),
      siret,
      siren,
      address: String(item.address || ""),
      postal_code: String(item.postal_code || ""),
      city: String(item.city || ""),
      status,
    }];
  });
}

export type FxLink = { company_id: number; client_id: number };

export async function listFxLinks(ficheId: number): Promise<FxLink[]> {
  const res = await fetch(`${apiUrl("fx-clients.php")}${currentUserQuery()}${currentUserQuery() ? "&" : "?"}fiche_id=${ficheId}`, {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) throw new Error(String(data.message || "Clients Factures indisponibles"));
  return (data.clients as FxLink[]) || [];
}

export async function addFxLink(ficheId: number, companyId: number, clientId: number): Promise<FxLink[]> {
  const res = await fetch(apiUrl("fx-clients.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ fiche_id: ficheId, company_id: companyId, client_id: clientId }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) throw new Error(String(data.message || "Client non ajouté"));
  return (data.clients as FxLink[]) || [];
}

export async function removeFxLink(ficheId: number, clientId: number): Promise<FxLink[]> {
  const res = await fetch(apiUrl("fx-clients.php") + currentUserQuery(), {
    method: "DELETE",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ fiche_id: ficheId, client_id: clientId }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) throw new Error(String(data.message || "Client non retiré"));
  return (data.clients as FxLink[]) || [];
}
